/**
 * 深色商务模板 - 详情页
 * 
 * 设计风格：
 * - 图片放在详情内容下方，无轮播，直接纵向排列
 * - 联系方式直接显示，无需点击查看，采用拨号/复制按钮交互
 */

import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function DarkListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => navigate("/category", { replace: true }))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => {
        const filtered = (data.list || []).filter(l => l.id !== id).slice(0, 4);
        setRelatedListings(filtered);
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images && listing.images.length > 0 ? listing.images : [];
  const hasContact = listing.contact_phone || listing.contact_wechat;

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />

      {/* ── 面包屑 ── */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs text-gray-700" />
          <Link to="/category" className="text-gray-500 hover:text-gray-300 transition-colors">分类</Link>
          {listing.category_name && (
            <>
              <i className="ri-arrow-right-s-line text-xs text-gray-700" />
              <span className="text-gray-500">{listing.category_name}</span>
            </>
          )}
          <i className="ri-arrow-right-s-line text-xs text-gray-700" />
          <span className="text-gray-300 font-medium truncate">{listing.title}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* ── 标题卡片 ── */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 relative overflow-hidden mb-6">
          {/* 顶部金色边框线 */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {listing.category_name && (
                  <span className="px-2.5 py-0.5 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-lg border border-amber-500/20">
                    {listing.category_name}
                  </span>
                )}
                {listing.is_pinned && (
                  <span className="px-2.5 py-0.5 bg-amber-500 text-gray-900 text-xs font-bold rounded-lg flex items-center gap-1">
                    <i className="ri-pushpin-line" />置顶
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight">
                {listing.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-gray-600" />
                  {new Date(listing.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-gray-600" />
                  {listing.view_count} 次浏览
                </span>
              </div>
            </div>
            <button
              onClick={() => setFavorited(!favorited)}
              className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-xl transition-all cursor-pointer border ${
                favorited
                  ? "bg-red-500/10 border-red-500/30 text-red-400"
                  : "bg-gray-800 border-gray-700 text-gray-600 hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5"
              }`}
            >
              <i className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`} />
            </button>
          </div>
        </div>

        {/* ── 联系方式 ── 发布者信息 + 电话微信在一起 ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* 左侧：发布者信息 + 联系方式 */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
            {/* 发布者 */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-600/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                <i className="ri-user-3-line text-sm" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-200 text-sm">{listing.contact_name || '匿名用户'}</p>
                {listing.company_name && !siteConfig?.hide_company_name && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <i className="ri-building-line" />{listing.company_name}
                  </p>
                )}
              </div>
            </div>

            {/* 电话 / 微信 */}
            {hasContact ? (
              <div className="space-y-2.5 pt-3 border-t border-gray-800">
                {listing.contact_phone && (
                  <a
                    href={`tel:${listing.contact_phone}`}
                    className="flex items-center justify-between gap-3 py-2.5 px-3 bg-gray-800 border border-gray-700 rounded-xl hover:border-amber-500/30 transition-all cursor-pointer group"
                  >
                    <span className="flex items-center gap-2.5">
                      <i className="ri-phone-line text-amber-400 text-base" />
                      <span className="text-gray-200 font-semibold text-sm">{listing.contact_phone}</span>
                    </span>
                    <span className="text-xs bg-amber-500/10 text-amber-400 px-2.5 py-1 rounded-lg border border-amber-500/20 shrink-0 group-hover:bg-amber-500/20 transition-colors">
                      <i className="ri-phone-fill mr-1" />一键拨号
                    </span>
                  </a>
                )}
                {listing.contact_wechat && (
                  <div className="flex items-center justify-between gap-3 py-2.5 px-3 bg-gray-800 border border-gray-700 rounded-xl group">
                    <span className="flex items-center gap-2.5">
                      <i className="ri-wechat-line text-green-400 text-base" />
                      <span className="text-gray-200 font-semibold text-sm">{listing.contact_wechat}</span>
                    </span>
                    <span
                      className="text-xs bg-green-500/10 text-green-400 px-2.5 py-1 rounded-lg border border-green-500/20 shrink-0 cursor-pointer hover:bg-green-500/20 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(listing.contact_wechat).catch(() => {});
                      }}
                    >
                      <i className="ri-file-copy-line mr-1" />复制
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-2 pt-3 border-t border-gray-800">暂未提供联系方式</p>
            )}
          </div>

          {/* 右侧：服务区域 + 发布按钮 */}
          <div className="space-y-4">
            {listing.address && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                  <i className="ri-map-pin-line text-gray-500" />
                  服务区域
                </h3>
                <p className="text-gray-400 text-sm">{listing.address}</p>
              </div>
            )}
            <Link
              to="/publish"
              className="flex items-center justify-center gap-2 w-full py-3 bg-gray-900 text-amber-400 font-semibold text-sm rounded-xl border border-gray-800 hover:border-amber-500/30 hover:bg-amber-500/5 transition-all cursor-pointer"
            >
              <i className="ri-add-line" />
              发布类似服务
            </Link>
          </div>
        </div>

        {/* ── 详细介绍内容 ── */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-base font-bold text-white mb-5 flex items-center gap-2">
            <i className="ri-file-text-line text-amber-400" />
            详细介绍
          </h2>
          <div className="text-gray-400 leading-relaxed whitespace-pre-wrap">
            {listing.content || "暂无详细描述。"}
          </div>
        </div>

        {/* ── 图片 ── 直接在介绍下方 ── */}
        {images.length > 0 && (
          <div className="space-y-4 mb-6">
            {images.map((img: string, i: number) => (
              <div
                key={i}
                className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden"
              >
                <img
                  src={img}
                  alt={`${listing.title} - 图${i + 1}`}
                  className="w-full object-contain max-h-[600px]"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {/* ── 相关推荐 ── */}
        {relatedListings.length > 0 && (
          <section className="mt-10 pb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-extrabold text-white flex items-center gap-2">
                <span className="w-1 h-6 bg-amber-500 rounded-full" />
                相关推荐
              </h2>
              <Link to="/category" className="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedListings.map(item => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-amber-500/30 hover:shadow-lg hover:shadow-amber-500/5 transition-all duration-300"
                >
                  <div className="w-full h-32 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden relative">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-2xl text-gray-700" />
                      </div>
                    )}
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-200 text-xs line-clamp-1 group-hover:text-amber-400 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-amber-400/70 bg-amber-500/5 px-1.5 py-0.5 rounded-md border border-amber-500/10">
                        {item.category?.name}
                      </span>
                      <span className="text-xs text-gray-600">{formatShortDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 animate-pulse space-y-6">
        <div className="w-3/4 h-8 bg-gray-800 rounded-xl" />
        <div className="w-1/2 h-4 bg-gray-800 rounded" />
        <div className="w-full h-40 bg-gray-800 rounded-2xl" />
        <div className="w-full h-60 bg-gray-800 rounded-2xl" />
      </div>
    </div>
  );
}
